import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  ConditionModel,
  LeadAssignmentRuleFormModel,
} from '@user/components/Settings/LeadAssignmentRule/LeadAssignmentRuleForm/typings'

import { ServiceConfig } from '../services/config'
import { APIError, get, post, put, remove } from '../services/crud'
import { AssignmentRuleDTO } from './typings'

const baseUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.automatedAssignment.automatedAssignment}`

export const getAllRules = createAsyncThunk<
  AssignmentRuleDTO[],
  void,
  { rejectValue: APIError }
>('automatedAssignments/getAll', async (_, thunkApi) => {
  const response = await get(`${baseUrl}`)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const createAssignmentRule = createAsyncThunk<
  AssignmentRuleDTO,
  LeadAssignmentRuleFormModel,
  { rejectValue: APIError }
>('automatedAssignments/add', async (data, thunkApi) => {
  const response = await post(`${baseUrl}`, {
    ...data,
    assignmentRuleConditions: data.assignmentRuleConditions.map(
      (condition) => ({
        ...condition,
        assignmentRuleConditionValues:
          condition.assignmentRuleConditionValues.map(
            (value) => value.id || value,
          ),
      }),
    ),
  })

  if (response.ok) return await response.json()

  return thunkApi.rejectWithValue(await response.json())
})

export const updateAssignmentRule = createAsyncThunk<
  AssignmentRuleDTO,
  LeadAssignmentRuleFormModel
>('', async (data, thunkApi) => {
  const response = await put(`${baseUrl}`, data)

  if (response.ok) return await response.json()

  return thunkApi.rejectWithValue(await response.json())
})

export const validateAssignmentRuleCoverage = createAsyncThunk<
  void,
  { id: number; assignmentRuleConditions: ConditionModel[] },
  { rejectValue: APIError }
>('automatedAssignments/validateCoverage', async (data, thunkApi) => {
  const response = await put(
    `${baseUrl}${ServiceConfig.automatedAssignment.validateCoverage}`,
    {
      ...data,
      assignmentRuleConditions: data.assignmentRuleConditions.map(
        (condition) => ({
          ...condition,
          assignmentRuleConditionValues:
            condition.assignmentRuleConditionValues.map(
              (value) => value?.id || value?.abbreviation || value,
            ),
        }),
      ),
    },
  )

  if (response.ok) return

  return thunkApi.rejectWithValue(await response.json())
})

export const removeAssignmentRule = createAsyncThunk<
  number,
  number,
  { rejectValue: APIError }
>('automatedAssignments/delete', async (id, thunkApi) => {
  const response = await remove(`${baseUrl}/${id}`)

  if (response.ok) return id

  return thunkApi.rejectWithValue(await response.json())
})

export const searchMatchingRule = async (queryParams) => {
  const queryString = Object.entries(queryParams)
    .map((entry) => `${entry[0]}=${entry[1]}`)
    .join('&')
  const response = await get(`${baseUrl}/searchMatchingRule?${queryString}`)

  if (response.ok && response.status === 200) return response.json()
  if (response.ok && response.status === 204) return null

  throw response.json()
}

export const toggleAssignmentRulePause = createAsyncThunk<
  { id: number; toggleState: boolean },
  { id: number; toggleState: boolean },
  { rejectValue: APIError }
>(
  'automatedAssignments/updateAssignmentRulePause',
  async (params, thunkApi) => {
    const response = await put(`${baseUrl}/toggleRule`, params)

    if (response.ok) return params

    return thunkApi.rejectWithValue(await response.json())
  },
)

export const reorderAssignmentRules = createAsyncThunk<void, number[]>(
  'automatedAssignments/reorderRules',
  async (data, thunkApi) => {
    const response = await put(`${baseUrl}/reorderRules`, data)

    if (response.ok) return await response.json()

    return thunkApi.rejectWithValue(await response.json())
  },
)

export const bulkEnableDisable = createAsyncThunk<
  boolean,
  boolean,
  { rejectValue: APIError }
>('automatedAssignments/bulkEnableDisable', async (isEnable, thunkApi) => {
  const response = await put(`${baseUrl}/bulkEnableDisable`, { isEnable })

  if (response.ok) return isEnable

  return thunkApi.rejectWithValue(await response.json())
})
